// HospitalCityView — the "City view" tab on a hospital profile (2026-09-15).
//
// The neighbourhood: this hospital among the hospitals within 30 / 60 / 120 /
// 300 straight-line miles — on the same OpenStreetMap the /map page uses
// (letter-grade badge markers) and in a table with price grade, markup vs
// the national median, CMS stars, HCAHPS recommend %, and Google. So a
// reader sees who is around and how they grade before picking a procedure.
//
// The per-scenario half of this question — "is it cheaper this far away?" —
// lives on the Scenario Pricing tab as the City panel beside the scenario
// cards (ScenarioDistanceTool); this tab links there.
//
// Distances are haversine, NOT driving distance — the UI says so. A band
// with nobody in it renders "no nearby comparison", never "0 cheaper".
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Car, Compass, Filter as FilterIcon, Loader2, MapPin, Search, Star, X } from "lucide-react";
import { getHospitalGradeInfo } from "@/lib/grades";
import { titleCaseHospital } from "@/lib/hospitalName";
import { herePlausible, savingFloor } from "@/lib/localSavings";
import { tokenize, rankMatch, matchPosition } from "@/lib/searchMatch";
import { NEIGHBOUR_RADII, useHospitalNeighbours, useNeighbourGrades, type NeighbourRadius } from "@/hooks/useHospitalNeighbours";
import { getAllHospitals } from "@/lib/scenarioCoverageStore";
import { useHospitalScenarios, type HospitalScenarioRow } from "@/hooks/useHospitalScenarios";
import { useScenarioAlternatives } from "@/hooks/useScenarioAlternatives";
import { useCodeNeighbourPrices, FAMILY_LABEL, type CodeFamily } from "@/hooks/useCodeNeighbourPrices";
import { useHospitalCodeCatalog, type HospitalCodeRow } from "@/hooks/useHospitalCodeCatalog";
import type { DriveVerdict } from "@/lib/localSavings";

const NeighbourhoodMap = lazy(() => import("./NeighbourhoodMap"));

const NEIGHBOUR_TABLE_MAX = 25;
const MAP_MARKER_CAP = 1000;
// Floating table panel: its default width is the CSS min(720px, 48vw); these
// bound what a reader may drag it to, and where it sits in the stage.
const PANEL_W_KEY = "ahp:cityview:panelWidth";
const PANEL_MIN_W = 280;        // narrower than this and the panel header wraps to nonsense
const PANEL_MAP_GUTTER = 340;   // map + top-left filter card must stay reachable at any width
const PANEL_EDGE_GAP = 16;      // .cityview-panel's right inset
// The search now spans the map's uncovered half, so it can show a real list
// instead of the six-row peek the 400px card allowed.
const SCENARIO_HIT_CAP = 24;
const CODE_HIT_CAP = 24;
const serif = "'Instrument Serif', Georgia, serif";
const sans = "'Inter', system-ui, -apple-system, sans-serif";
const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";

function num(v: unknown): number | null { if (v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
const fmtMiles = (mi: number) => mi < 10 ? mi.toFixed(1) : String(Math.round(mi));
const fmtUSD = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtEr = (m: number) => `${Math.round(m)} min`;

function GradeBadge({ markup, size = 24 }: { markup: number | null; size?: number }) {
  const gi = getHospitalGradeInfo(markup);
  const letter = String(gi.grade);
  const graded = letter !== "N/A";
  return (
    <span aria-label={graded ? `Price grade ${letter}` : "Not ranked"} style={{
      width: size, height: size, borderRadius: Math.round(size * 0.23), flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: graded ? gi.color : "transparent", border: graded ? 0 : "1px dashed currentColor", color: graded ? "#fff" : "inherit", opacity: graded ? 1 : 0.5,
      fontWeight: 900, fontSize: Math.round(size * 0.55), lineHeight: 1, fontFamily: sans,
    }}>{graded ? letter : "–"}</span>
  );
}

export function HospitalCityView({ hospital, isDark, priceMarkup, priceRank, totalRanked }: {
  hospital: { id: string; name: string; city?: string | null; state?: string | null; latitude?: number | string | null; longitude?: number | string | null; cms_overall_rating?: number | string | null; google_rating?: number | string | null; google_review_count?: number | string | null };
  isDark: boolean;
  priceMarkup: number | null;
  priceRank: number | null;
  totalRanked: number | null;
}) {
  const { t } = useTranslation(["hospital"]);
  const tr = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    t(`hospital:cityView.${key}`, { defaultValue: fallback, ...(opts || {}) });

  const P = isDark ? {
    cardBg: "linear-gradient(180deg, rgba(2, 22, 60, 0.94) 0%, rgba(0, 14, 44, 0.97) 100%)", cardBorder: "rgba(var(--civic-gold-rgb), 0.22)",
    text: "var(--civic-parchment)", muted: "rgba(var(--civic-parchment-rgb), 0.62)", faded: "rgba(var(--civic-parchment-rgb), 0.42)",
    divider: "rgba(var(--civic-gold-rgb), 0.18)", hereBg: "rgba(var(--civic-gold-rgb), 0.10)", goldInk: "var(--civic-gold)", red: "var(--civic-red-soft, #E4566A)", goodInk: "#86efac",
    chipOn: "var(--civic-gold)", chipOnInk: "#1B1B1B", chipOff: "rgba(var(--civic-parchment-rgb), 0.06)",
    sheetBg: "rgba(2, 22, 60, 0.86)", sheetSolid: "rgb(2, 22, 60)", inputBg: "rgba(var(--civic-parchment-rgb), 0.06)",
  } : {
    cardBg: "linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)", cardBorder: "rgba(var(--civic-navy-deeper-rgb), 0.14)",
    text: "var(--civic-navy-deeper)", muted: "rgba(var(--civic-navy-deeper-rgb), 0.66)", faded: "rgba(var(--civic-navy-deeper-rgb), 0.5)",
    divider: "rgba(var(--civic-gold-rgb), 0.35)", hereBg: "rgba(var(--civic-gold-rgb), 0.14)", goldInk: "var(--civic-gold-ink)", red: "var(--civic-red)", goodInk: "#0F6B49",
    chipOn: "var(--civic-navy)", chipOnInk: "#FAF9F6", chipOff: "rgba(var(--civic-navy-deeper-rgb), 0.05)",
    sheetBg: "rgba(255, 255, 255, 0.86)", sheetSolid: "#FFFFFF", inputBg: "rgba(255,255,255,0.85)",
  };
  const card: React.CSSProperties = { background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 10, overflow: "hidden", color: P.text, fontFamily: sans };

  const lat = num(hospital.latitude), lng = num(hospital.longitude);
  const displayName = titleCaseHospital(hospital.name) || hospital.name;
  const place = [hospital.city ? titleCaseHospital(hospital.city) : null, hospital.state].filter(Boolean).join(", ");

  const { neighbours: hood, counts, isLoading: hoodLoading } = useHospitalNeighbours(hospital.id, lat, lng);
  const [hoodRadius, setHoodRadius] = useState<NeighbourRadius>(30);
  // Pick the tightest band with company by default (rural hospitals open at 60/120).
  useEffect(() => {
    if (!hood) return;
    setHoodRadius(NEIGHBOUR_RADII.find((r) => counts[r] >= 3) ?? 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hood]);
  const bandNeighbours = useMemo(() => (hood || []).filter((n) => n.miles <= hoodRadius).slice(0, MAP_MARKER_CAP), [hood, hoodRadius]);
  const tableNeighbours = useMemo(() => bandNeighbours.slice(0, NEIGHBOUR_TABLE_MAX), [bandNeighbours]);
  const gradeIds = useMemo(() => bandNeighbours.map((n) => n.id), [bandNeighbours]);
  const { markupOf, rankOf } = useNeighbourGrades(gradeIds);
  // "Here" row's CMS stars / recommend % / ER wait / Google from the same
  // manifest the neighbours use, so the columns compare like with like.
  const hereInfo = useMemo(() => (hood ? getAllHospitals()[hospital.id] : undefined), [hood, hospital.id]);
  const hoodById = useMemo(() => new Map((hood || []).map((n) => [n.id, n] as const)), [hood]);

  type HoodSort = "distance" | "price" | "quality";
  const [hoodSort, setHoodSort] = useState<HoodSort>("distance");
  // ≥1024px: map fills the viewport and the table floats over its right side.
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Resizable table panel (Trent, 2026-09-22) ─────────────────────────
  // The table floats over the map's right side at min(720px, 48vw), which
  // is too narrow for its seven columns on a small laptop and too wide when
  // a reader wants to see the map. A grip on the panel's left edge drags it
  // wider or narrower; the width persists per browser and double-clicking
  // (or Enter on the focused grip) resets it to the default. Desktop only —
  // under 1024px the panel drops into flow under the map and the grip hides.
  //
  // The map is NOT re-framed on resize: it fills the stage regardless of the
  // panel's width, and re-centring would throw away the reader's own pan.
  const stageRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const [panelW, setPanelWState] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const n = Number(window.localStorage.getItem(PANEL_W_KEY));
      return Number.isFinite(n) && n >= PANEL_MIN_W ? n : null;
    } catch { return null; }
  });
  const panelWRef = useRef<number | null>(panelW);
  const [resizing, setResizing] = useState(false);
  // The panel's real rendered width — the default is a CSS min(), so it is
  // only knowable by measurement. Feeds the grip's aria-valuenow.
  const [panelActualW, setPanelActualW] = useState<number | null>(null);
  useEffect(() => {
    const el = panelRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setPanelActualW(Math.round(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The stage is full-bleed 100vw; tracking its width in state is what makes
  // the render-time clamp below recompute when the window changes.
  const [stageW, setStageW] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setStageW(Math.round(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Widest the panel may get: the stage less a strip that keeps the map and
  // the top-left filter card reachable.
  const panelMaxW = () => Math.max(PANEL_MIN_W, Math.round((stageRef.current?.getBoundingClientRect().width || stageW) - PANEL_MAP_GUTTER));
  const clampPanelW = (w: number) => Math.round(Math.min(panelMaxW(), Math.max(PANEL_MIN_W, w)));
  const setPanelW = (w: number | null) => { panelWRef.current = w; setPanelWState(w); };
  const persistPanelW = (w: number | null) => {
    try {
      if (w == null) window.localStorage.removeItem(PANEL_W_KEY);
      else window.localStorage.setItem(PANEL_W_KEY, String(w));
    } catch { /* private mode — the width just won't survive a reload */ }
  };
  const resetPanelW = () => { setPanelW(null); persistPanelW(null); };
  const nudgePanelW = (delta: number) => {
    const el = panelRef.current;
    const current = panelWRef.current ?? (el ? el.getBoundingClientRect().width : PANEL_MIN_W);
    const next = clampPanelW(current + delta);
    setPanelW(next); persistPanelW(next);
  };

  // The stored width is a PREFERENCE, never rewritten by a window resize —
  // it is clamped at render instead. So narrowing the window (or rotating
  // into the mobile layout and back) shows a legal width now and gives the
  // reader their own width back the moment there is room for it again.
  const effectivePanelW = wide && panelW != null ? clampPanelW(panelW) : null;

  const onGripPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!wide || e.button !== 0) return;
    e.preventDefault();          // no text selection, no drag-image
    e.currentTarget.focus();     // preventDefault skips click-focus; arrow keys should work after a drag
    setResizing(true);
    document.body.classList.add("cityview-resizing");
    // Where inside the grip the pointer landed, so the panel edge tracks the
    // pointer from the first pixel instead of snapping to it.
    const grabOffset = e.clientX - (panelRef.current?.getBoundingClientRect().left ?? e.clientX);
    const move = (ev: PointerEvent) => {
      // The panel is pinned PANEL_EDGE_GAP from the stage's right edge, so its
      // width is the gap between that edge and its left edge — the pointer
      // less the grab offset.
      const right = stageRef.current?.getBoundingClientRect().right ?? window.innerWidth;
      setPanelW(clampPanelW(right - PANEL_EDGE_GAP - (ev.clientX - grabOffset)));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      document.body.classList.remove("cityview-resizing");
      setResizing(false);
      persistPanelW(panelWRef.current);
    };
    // On window, not on the grip: a re-render that replaces the grip node
    // mid-drag (neighbour data landing, say) would silently strip listeners
    // bound to it, and the drag would die with the body still locked.
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };
  // If the tab is switched away mid-drag the pointerup never reaches us, so
  // make sure the page doesn't keep the col-resize cursor and the selection lock.
  useEffect(() => () => { document.body.classList.remove("cityview-resizing"); }, []);

  const onGripKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 96 : 24;
    // The panel sits on the right, so dragging the separator left widens it.
    if (e.key === "ArrowLeft") { e.preventDefault(); nudgePanelW(step); }
    else if (e.key === "ArrowRight") { e.preventDefault(); nudgePanelW(-step); }
    else if (e.key === "Enter" || e.key === "Home") { e.preventDefault(); resetPanelW(); }
  };

  // ── Map filter: ONE scenario or ONE billing code at a time (Trent, 2026-09-16) ──
  // Scenario → every neighbour's complete bill for it (coverage shard) with
  // the drive verdict; code → each neighbour's median listed charge for that
  // CPT / HCPCS / MS-DRG / REV (hospital_procedure_analytics). The map
  // recolors, the sheet's table switches to prices, and hospitals with no
  // price on file are dimmed and counted — never shown as $0.
  type MapFilter = { kind: "scenario"; id: string; label: string } | { kind: "code"; fam: CodeFamily; code: string; label: string } | null;
  const [filter, setFilter] = useState<MapFilter>(null);
  const [fq, setFq] = useState("");
  const [fOpen, setFOpen] = useState(false);
  const [showUnquotable, setShowUnquotable] = useState(false);
  const { scenarios: hospScenarios, isLoading: scenariosLoading } = useHospitalScenarios(hospital.id);
  const { data: catalog, isLoading: catalogLoading } = useHospitalCodeCatalog(hospital.id, fOpen || filter?.kind === "code");
  const pickedScenario = useMemo(() => filter?.kind === "scenario" ? hospScenarios.find((s) => s.scenario_id === filter.id) : undefined, [filter, hospScenarios]);
  const { data: alternatives, isLoading: altLoading } = useScenarioAlternatives(hospital.id, filter?.kind === "scenario" ? filter.id : null, pickedScenario, hood);
  const { data: codePrices, isLoading: codeLoading } = useCodeNeighbourPrices(filter?.kind === "code" ? filter.fam : null, filter?.kind === "code" ? filter.code : null, hospital.id, gradeIds);
  const filterLoading = filter?.kind === "scenario" ? (scenariosLoading || altLoading) : filter?.kind === "code" ? codeLoading : false;
  const verdictMap = useMemo(() => alternatives?.length ? new Map(alternatives.map((a) => [a.id, { verdict: a.verdict, price: a.price }] as const)) : null, [alternatives]);
  const herePrice: number | null = filter?.kind === "scenario" ? (pickedScenario?.blendedPrice ?? null) : filter?.kind === "code" ? (codePrices?.here ?? null) : null;
  const nationalPrice: number | null = filter?.kind === "scenario" ? (pickedScenario?.nationalCost ?? null) : filter?.kind === "code" ? (codePrices?.nationalMedian ?? null) : null;
  interface FilterRow { id: string; name: string; place: string; miles: number; price: number; verdict: DriveVerdict | null; cs: number | null; ew: number | null }
  const filterRows = useMemo<{ rows: FilterRow[]; unquotable: number } | null>(() => {
    if (filter?.kind === "scenario") {
      const inBand = (alternatives || []).filter((a) => a.miles <= hoodRadius);
      return {
        rows: inBand.filter((a) => showUnquotable || a.verdict.kind !== "unquotable").map((a) => ({ id: a.id, name: a.name, place: [a.city, a.state].filter(Boolean).join(", "), miles: a.miles, price: a.price, verdict: a.verdict, cs: a.cs, ew: hoodById.get(a.id)?.ew ?? null })),
        unquotable: inBand.filter((a) => a.verdict.kind === "unquotable").length,
      };
    }
    if (filter?.kind === "code") {
      if (!codePrices) return { rows: [], unquotable: 0 };
      const rows: FilterRow[] = [];
      for (const n of bandNeighbours) {
        const p = codePrices.prices.get(n.id);
        if (p == null) continue;
        rows.push({ id: n.id, name: titleCaseHospital(n.n) || n.n, place: [titleCaseHospital(n.c) || n.c, n.s].filter(Boolean).join(", "), miles: n.miles, price: p, verdict: null, cs: n.cs ?? null, ew: n.ew ?? null });
      }
      return { rows, unquotable: 0 };
    }
    return null;
  }, [filter, alternatives, codePrices, bandNeighbours, hoodRadius, showUnquotable, hoodById]);
  // A–F for the active filter: this price vs the U.S. figure, our median-baseline scale.
  const procMarkup = (p: number | null): number | null => (p != null && nationalPrice != null && nationalPrice > 0) ? ((p - nationalPrice) / nationalPrice) * 100 : null;
  const sortedFilterRows = useMemo(() => {
    if (!filterRows) return [] as FilterRow[];
    const rows = [...filterRows.rows];
    if (hoodSort === "price") rows.sort((a, b) => a.price - b.price);
    else if (hoodSort === "quality") rows.sort((a, b) => (b.cs ?? -1) - (a.cs ?? -1) || a.price - b.price);
    else rows.sort((a, b) => a.miles - b.miles);
    return rows;
  }, [filterRows, hoodSort]);
  const fqn = fq.trim().toLowerCase();
  const fTokens = useMemo(() => tokenize(fqn), [fqn]);

  // Lowercase each record ONCE per list rather than once per keystroke — the
  // catalogue runs to tens of thousands of codes.
  const scenarioIndex = useMemo(
    () => hospScenarios.map((s) => ({ row: s, name: s.scenario.name.toLowerCase(), hay: `${s.scenario.name} ${s.scenario.category || ""}`.toLowerCase() })),
    [hospScenarios],
  );
  const codeIndex = useMemo(
    () => (catalog || []).map((r) => ({ row: r, code: r.code.toLowerCase().replace(/^ms-/, ""), desc: (r.desc || "").toLowerCase(), hay: `${r.code} ${r.desc || ""} ${FAMILY_LABEL[r.fam]}`.toLowerCase() })),
    [catalog],
  );

  const scenarioHits = useMemo(() => {
    if (fqn.length < 2) return [...hospScenarios].sort((a, b) => (a.usa_rank_expensive ?? Infinity) - (b.usa_rank_expensive ?? Infinity)).slice(0, SCENARIO_HIT_CAP);
    // Scenarios whose price HERE passes the plausibility gate first (a
    // defective total makes every neighbour unquotable), then by match
    // quality, then by how early the match sits in the name.
    const hits: { row: HospitalScenarioRow; rank: number; pos: number }[] = [];
    for (const e of scenarioIndex) {
      const rank = rankMatch(e.name, e.hay, fqn, fTokens);
      if (rank >= 0) hits.push({ row: e.row, rank, pos: matchPosition(e.name, fTokens) });
    }
    hits.sort((a, b) =>
      Number(herePlausible(b.row.blendedPrice, b.row.nationalCost)) - Number(herePlausible(a.row.blendedPrice, a.row.nationalCost))
      || a.rank - b.rank || a.pos - b.pos || a.row.scenario.name.localeCompare(b.row.scenario.name));
    return hits.slice(0, SCENARIO_HIT_CAP).map((h) => h.row);
  }, [hospScenarios, scenarioIndex, fqn, fTokens]);

  const codeHits = useMemo(() => {
    if (!codeIndex.length || fqn.length < 2) return [] as HospitalCodeRow[];
    const q = fqn.replace(/^ms-/, "");
    const hits: { row: HospitalCodeRow; rank: number; pos: number }[] = [];
    for (const e of codeIndex) {
      // A typed code still wins outright over any description match.
      let rank: number;
      if (e.code === q) rank = 0;
      else if (e.code.startsWith(q)) rank = 1;
      else {
        const r = rankMatch(e.desc, e.hay, fqn, fTokens);
        rank = r < 0 ? -1 : r + 2;   // description hits always rank below a typed code
      }
      if (rank >= 0) hits.push({ row: e.row, rank, pos: matchPosition(e.desc, fTokens) });
    }
    hits.sort((a, b) => a.rank - b.rank || a.pos - b.pos || a.row.code.localeCompare(b.row.code));
    return hits.slice(0, CODE_HIT_CAP).map((h) => h.row);
  }, [codeIndex, fqn, fTokens]);
  const pickScenario = (sc: HospitalScenarioRow) => { setFilter({ kind: "scenario", id: sc.scenario_id, label: sc.scenario.name }); setFq(""); setFOpen(false); setHoodSort("price"); setFocusId(null); };
  const pickCode = (r: HospitalCodeRow) => { setFilter({ kind: "code", fam: r.fam, code: r.code, label: `${FAMILY_LABEL[r.fam]} ${r.code} · ${r.desc || ""}` }); setFq(""); setFOpen(false); setHoodSort("price"); setFocusId(null); };
  const clearFilter = () => { setFilter(null); setHoodSort("distance"); setShowUnquotable(false); setFocusId(null); };
  const mapColorBy = filter?.kind === "scenario" && verdictMap ? "verdict" : filter?.kind === "code" && codePrices ? "price" : "grade";
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  // Row click: highlight it, centre the map on that hospital and open its
  // popup (price/grade for the active filter). Profile opens via its button.
  const [focusId, setFocusId] = useState<string | null>(null);
  const focusRow = (id: string) => { setHighlightId(id); setFocusId(id); };
  const selectFromMap = (id: string) => {
    setHighlightId(id);
    const row = rowRefs.current.get(id);
    if (row) row.scrollIntoView({ block: "nearest", behavior: "smooth" });
  };
  const sortedNeighbours = useMemo(() => {
    const rows = [...tableNeighbours];
    if (hoodSort === "price") rows.sort((a, b) => (markupOf(a.id) ?? Infinity) - (markupOf(b.id) ?? Infinity));
    else if (hoodSort === "quality") rows.sort((a, b) => (b.cs ?? -1) - (a.cs ?? -1) || (b.rp ?? -1) - (a.rp ?? -1));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableNeighbours, hoodSort, markupOf]);
  const cheaperThanHere = useMemo(() => {
    if (priceMarkup == null) return null;
    let n = 0, total = 0;
    for (const nb of tableNeighbours) { const m = markupOf(nb.id); if (m == null) continue; total++; if (m < priceMarkup) n++; }
    return total ? { n, total } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableNeighbours, markupOf, priceMarkup]);

  const segStyle = (on: boolean): React.CSSProperties => ({
    padding: "5px 11px", borderRadius: 999, border: `1px solid ${on ? "transparent" : P.cardBorder}`, cursor: "pointer",
    background: on ? P.chipOn : P.chipOff, color: on ? P.chipOnInk : P.text, fontFamily: sans, fontSize: 12, fontWeight: 700,
  });

  const groupHead: React.CSSProperties = { padding: "8px 4px 4px", fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: P.goldInk };
  const hint: React.CSSProperties = { padding: "4px 4px 8px", fontSize: 11.5, color: P.muted };
  const profileBtn: React.CSSProperties = { flexShrink: 0, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", color: P.goldInk, textDecoration: "none", border: `1px solid ${P.cardBorder}`, borderRadius: 999, padding: "1px 7px", whiteSpace: "nowrap" };
  const resultBtn: React.CSSProperties = { display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, borderTop: `1px solid ${P.divider}`, padding: "7px 4px", cursor: "pointer", fontFamily: sans, fontSize: 12.5, lineHeight: 1.3 };

  if (lat == null || lng == null) {
    return (
      <div style={{ ...card, padding: 20 }}>
        <p style={{ margin: 0, color: P.muted }}>{tr("noCoords", "We don't have coordinates for this hospital yet, so the city view can't be drawn.")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Full-bleed stage (Trent, 2026-09-16): the map fills the viewport
          edge to edge, and the neighbour table floats over its right side
          instead of sitting under it. Under 1024px the panel drops back
          below a 60vh map — a 100vh map with a floating sheet has no room
          on a phone. html has overflow-x: hidden, so the 100vw bleed can't
          add a horizontal scrollbar. */}
      <style>{`
        .cityview-stage { position: relative; width: 100vw; margin-left: calc(50% - 50vw); height: calc(100vh - var(--ahp-chrome-h, 0px)); min-height: 560px; }
        .cityview-stage .cityview-map { position: absolute; inset: 0; }
        .cityview-stage .cityview-map .leaflet-container { border-radius: 0 !important; }
        .cityview-panel { position: absolute; top: 16px; right: 16px; bottom: 16px; width: min(720px, 48vw); z-index: 1100; display: flex; flex-direction: column; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
        .cityview-panel-scroll { flex: 1 1 auto; min-height: 0; overflow: auto; }
        /* Full width of the map's uncovered half: the left inset is fixed and
           the right one is set inline from the table panel's current width, so
           widening the table narrows the search and vice versa. 12k scenarios
           and every code at this hospital need the room. */
        .cityview-filter { position: absolute; top: 16px; left: 16px; z-index: 1100; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
        .cityview-results { margin-top: 8px; overflow-y: auto; overscroll-behavior: contain; }
        /* Two columns once there is room, so a long hit list stays scannable. */
        .cityview-hitgrid { display: grid; grid-template-columns: 1fr; gap: 2px; }
        @media (min-width: 1280px) { .cityview-hitgrid { grid-template-columns: 1fr 1fr; } }
        /* Drag grip on the panel's left edge. It lives INSIDE the panel because
           the panel clips to its rounded corners (overflow: hidden), so a grip
           hung outside would be invisible. 10px wide — enough to grab, narrow
           enough to sit in the table's own 16px left padding. */
        .cityview-grip { position: absolute; top: 0; left: 0; bottom: 0; width: 10px; z-index: 3; cursor: col-resize; touch-action: none; display: flex; align-items: center; justify-content: center; }
        .cityview-grip::before { content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 2px; background: currentColor; opacity: 0; transition: opacity 150ms ease; }
        .cityview-grip-bar { width: 3px; height: 40px; border-radius: 999px; background: currentColor; opacity: 0.3; transition: opacity 150ms ease, height 150ms ease; pointer-events: none; }
        .cityview-grip:hover::before, .cityview-grip:focus-visible::before, .cityview-grip[data-resizing="true"]::before { opacity: 0.5; }
        .cityview-grip:hover .cityview-grip-bar, .cityview-grip:focus-visible .cityview-grip-bar, .cityview-grip[data-resizing="true"] .cityview-grip-bar { opacity: 0.85; height: 64px; }
        .cityview-grip:focus-visible { outline: 2px solid currentColor; outline-offset: -2px; }
        /* Hold the col-resize cursor and kill text selection for the whole drag. */
        body.cityview-resizing, body.cityview-resizing * { cursor: col-resize !important; user-select: none !important; }
        /* Filter card owns the top-left; Leaflet's zoom control moves to the bottom-left above the legend. */
        @media (min-width: 1024px) { .cityview-stage .leaflet-top.leaflet-left { top: auto; bottom: 200px; } }
        /* The floating sheet covers the right edge, so the legend (prop) and
           Leaflet's OSM attribution move to the bottom-left on desktop. */
        @media (min-width: 1024px) { .cityview-stage .leaflet-bottom.leaflet-right { right: auto; left: 0; } }
        @media (max-width: 1023px) {
          .cityview-stage { height: auto; min-height: 0; }
          .cityview-stage .cityview-map { position: relative; height: 60vh; min-height: 360px; }
          .cityview-panel { position: relative; top: auto; right: auto; bottom: auto; width: auto !important; margin: 0; border-radius: 0; backdrop-filter: none; -webkit-backdrop-filter: none; }
          .cityview-grip { display: none; }
          .cityview-filter { position: relative; top: auto; left: auto; right: auto !important; width: auto; margin: 0 0 8px; backdrop-filter: none; -webkit-backdrop-filter: none; }
          .cityview-hitgrid { grid-template-columns: 1fr; }
          .cityview-panel-scroll { overflow: visible; }
        }
      `}</style>
      <div>
        <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: 24, margin: 0, color: P.text }}>{tr("heading", "City view")}</h2>
        <p style={{ margin: "4px 0 0", fontSize: 13.5, color: P.muted }}>
          {tr("sub", "{{name}} among its neighbours — what the same care costs down the street, and when a drive is worth it. Distances are straight-line, not driving miles.", { name: displayName })}
        </p>
      </div>

      <section ref={stageRef} className="cityview-stage" aria-label={tr("hood.aria", "Nearby hospitals")} style={{ color: P.text, fontFamily: sans }}>
        <div
          className="cityview-filter"
          style={{
            ...card, background: P.sheetBg, boxShadow: "0 12px 36px rgba(0,0,0,0.2)",
            // Stop where the table panel starts (its width + its 16px inset + a 16px gap).
            ...(wide ? { right: (effectivePanelW ?? Math.min(720, stageW * 0.48)) + 32 } : null),
            transition: resizing ? "none" : "right 160ms ease",
          }}
        >
          <div style={{ padding: "10px 12px 10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: P.goldInk }}>
              <FilterIcon style={{ width: 13, height: 13 }} /> {tr("filter.eyebrow", "Filter the map")}
              {filter && (
                <button type="button" onClick={clearFilter} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, border: `1px solid ${P.cardBorder}`, borderRadius: 999, background: "transparent", color: P.text, cursor: "pointer", padding: "2px 8px", fontFamily: sans, fontSize: 10.5, fontWeight: 700, letterSpacing: 0, textTransform: "none" }}>
                  <X style={{ width: 11, height: 11 }} /> {tr("filter.clear", "Clear")}
                </button>
              )}
            </div>
            {filter ? (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontFamily: serif, fontSize: 17, lineHeight: 1.2, color: P.text }}>{filter.label}</div>
                <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: "4px 14px", fontSize: 12, color: P.muted }}>
                  <span>{tr("filter.here", "Here:")} <b style={{ fontFamily: mono, color: P.text }}>{herePrice != null ? fmtUSD(herePrice) : tr("filter.noPriceHere", "no price on file")}</b></span>
                  {nationalPrice != null && <span>{filter.kind === "scenario" ? tr("filter.usAvg", "U.S. avg:") : tr("filter.usMedian", "U.S. median:")} <b style={{ fontFamily: mono, color: P.text }}>{fmtUSD(nationalPrice)}</b></span>}
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: P.muted, display: "flex", alignItems: "center", gap: 6 }}>
                  {filterLoading
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {tr("filter.loading", "Pricing the neighbourhood…")}</>
                    : filterRows
                      ? <span>
                          {tr("filter.priced", "{{n}} of {{total}} hospitals within {{r}} mi price this{{extra}}", { n: (showUnquotable ? filterRows.rows.length : filterRows.rows.length + filterRows.unquotable), total: bandNeighbours.length, r: hoodRadius, extra: filterRows.unquotable ? tr("filter.unquotableSuffix", " ({{u}} fail the plausibility gate)", { u: filterRows.unquotable }) : "" })}
                          {filterRows.unquotable > 0 && (
                            <button type="button" onClick={() => setShowUnquotable((v) => !v)} aria-pressed={showUnquotable} style={{ marginLeft: 6, background: "transparent", border: 0, padding: 0, cursor: "pointer", fontFamily: sans, fontSize: 12, fontWeight: 700, color: P.goldInk, textDecoration: "underline", textUnderlineOffset: 3 }}>
                              {showUnquotable ? tr("filter.hideUnquotable", "hide them") : tr("filter.showUnquotable", "show them anyway")}
                            </button>
                          )}
                        </span>
                      : null}
                </div>
                <div style={{ marginTop: 4, fontSize: 10.5, color: P.faded }}>
                  {filter.kind === "scenario"
                    ? tr("filter.scenarioNote", "Complete-bill estimates from each hospital's own published file. A saving is only named when it clears the $500 / 20% bar.")
                    : tr("filter.codeNote", "Median of each hospital's listed charges for this code, as published. Unit conventions can differ between hospitals.")}
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 8 }}>
                <div style={{ position: "relative" }}>
                  <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: P.muted, pointerEvents: "none" }} />
                  <input
                    value={fq}
                    onChange={(e) => { setFq(e.target.value); setFOpen(true); }}
                    onFocus={() => setFOpen(true)}
                    onKeyDown={(e) => { if (e.key === "Escape") { setFOpen(false); setFq(""); (e.target as HTMLInputElement).blur(); } }}
                    placeholder={tr("filter.placeholder", "Scenario, CPT, HCPCS, MS-DRG or REV code…")}
                    aria-label={tr("filter.aria", "Filter the map by a scenario or billing code")}
                    style={{ width: "100%", padding: "8px 30px 8px 30px", borderRadius: 8, border: `1px solid ${P.cardBorder}`, background: P.inputBg, color: P.text, fontFamily: sans, fontSize: 13, outline: "none" }}
                  />
                  {fOpen && (
                    <button type="button" onClick={() => { setFOpen(false); setFq(""); }} aria-label={tr("filter.close", "Close")} style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", border: 0, background: "transparent", cursor: "pointer", color: P.muted, padding: 4 }}>
                      <X style={{ width: 14, height: 14 }} />
                    </button>
                  )}
                </div>
                {fOpen && (
                  <div className="cityview-results" style={{ maxHeight: wide ? "min(58vh, 520px)" : 320 }}>
                    <div style={groupHead}>
                      {tr("filter.scenariosGroup", "Scenarios priced at this hospital")}
                      {fqn.length >= 2 && !scenariosLoading && scenarioHits.length > 0 && (
                        <span style={{ marginLeft: 6, fontWeight: 500, color: P.faded }}>
                          {scenarioHits.length >= SCENARIO_HIT_CAP ? tr("filter.topN", "top {{n}}", { n: SCENARIO_HIT_CAP }) : tr("filter.nMatch", "{{n}} match", { n: scenarioHits.length })}
                        </span>
                      )}
                    </div>
                    {scenariosLoading ? <div style={hint}>{tr("filter.loadingScenarios", "Loading scenarios…")}</div>
                      : scenarioHits.length === 0 ? <div style={hint}>{tr("filter.noScenario", "No scenario matches.")}</div>
                      : (
                        <div className="cityview-hitgrid">
                          {scenarioHits.map((sc) => (
                            <button key={sc.scenario_id} type="button" onClick={() => pickScenario(sc)} style={resultBtn} className="hover:bg-black/[0.04] dark:hover:bg-white/[0.05]">
                              <span style={{ display: "block", fontWeight: 600, color: P.text }}>{sc.scenario.name}</span>
                              <span style={{ display: "block", fontSize: 11, color: P.muted }}>{sc.scenario.category} · {tr("filter.hereShort", "here")} {fmtUSD(sc.blendedPrice)} · {tr("filter.usAvgShort", "U.S. avg")} {fmtUSD(sc.nationalCost)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    <div style={groupHead}>
                      {tr("filter.codesGroup", "Billing codes at this hospital")}
                      {fqn.length >= 2 && !catalogLoading && codeHits.length > 0 && (
                        <span style={{ marginLeft: 6, fontWeight: 500, color: P.faded }}>
                          {codeHits.length >= CODE_HIT_CAP ? tr("filter.topN", "top {{n}}", { n: CODE_HIT_CAP }) : tr("filter.nMatch", "{{n}} match", { n: codeHits.length })}
                        </span>
                      )}
                    </div>
                    {fqn.length < 2 ? <div style={hint}>{tr("filter.typeCode", "Type a code or a few letters of its description — CPT, HCPCS, MS-DRG or REV. Words can be in any order.")}</div>
                      : catalogLoading ? <div style={hint}>{tr("filter.loadingCodes", "Loading this hospital's code list…")}</div>
                      : codeHits.length === 0 ? <div style={hint}>{tr("filter.noCode", "No code at this hospital matches.")}</div>
                      : (
                        <div className="cityview-hitgrid">
                          {codeHits.map((r) => (
                            <button key={`${r.fam}:${r.code}`} type="button" onClick={() => pickCode(r)} style={resultBtn} className="hover:bg-black/[0.04] dark:hover:bg-white/[0.05]">
                              <span style={{ display: "block", fontWeight: 600, color: P.text }}><span style={{ fontFamily: mono }}>{FAMILY_LABEL[r.fam]} {r.code}</span> · {r.desc || tr("filter.noDesc", "no description")}</span>
                              <span style={{ display: "block", fontSize: 11, color: P.muted }}>{r.here != null ? `${tr("filter.listedHere", "listed here")} ${fmtUSD(r.here)}` : tr("filter.noPriceHere", "no price on file")}{r.national != null ? ` · ${tr("filter.usAvgShort", "U.S. avg")} ${fmtUSD(r.national)}` : ""}</span>
                            </button>
                          ))}
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="cityview-map">
          <Suspense fallback={<div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: P.muted, fontSize: 12 }}><Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 8 }} /> {tr("map.loading", "Loading map…")}</div>}>
            <NeighbourhoodMap
              center={{ lat, lng }}
              hospital={{ id: hospital.id, name: hospital.name, city: hospital.city, state: hospital.state, markup: priceMarkup, rank: priceRank, cmsStars: num(hospital.cms_overall_rating), googleRating: num(hospital.google_rating), googleReviews: num(hospital.google_review_count) }}
              neighbours={bandNeighbours}
              radius={hoodRadius}
              isDark={isDark}
              markupOf={markupOf}
              rankOf={rankOf}
              totalRanked={totalRanked}
              colorBy={mapColorBy}
              verdicts={verdictMap}
              priceMap={codePrices?.prices ?? null}
              herePrice={herePrice}
              national={nationalPrice}
              gradeByPrice
              focusId={focusId}
              priceLabel={filter?.kind === "code" ? `${FAMILY_LABEL[filter.fam]} ${filter.code}` : undefined}
              highlightId={highlightId}
              onSelect={selectFromMap}
              heightClass="h-full"
              zoomOffset={wide ? 1 : 0}
              // On desktop the sheet covers the right ~46vw, so nudge the
              // centre left to keep the ring in the uncovered half.
              centerOffsetX={wide ? -0.24 : 0}
              legendSide={wide ? "left" : "right"}
            />
          </Suspense>
        </div>

        <aside
          ref={panelRef}
          className="cityview-panel"
          style={{
            ...card, background: P.sheetBg, boxShadow: "0 18px 48px rgba(0,0,0,0.22)",
            ...(effectivePanelW != null ? { width: effectivePanelW } : null),
            // Animate only the keyboard/reset jumps — a transition during a
            // drag makes the edge lag the pointer.
            transition: resizing ? "none" : "width 160ms ease",
          }}
        >
          <div
            className="cityview-grip"
            role="separator"
            aria-orientation="vertical"
            aria-label={tr("resize.aria", "Resize the nearby-hospitals table — drag, or use the arrow keys")}
            aria-valuenow={panelActualW ?? undefined}
            aria-valuemin={PANEL_MIN_W}
            aria-valuemax={panelMaxW()}
            title={tr("resize.title", "Drag to resize · double-click to reset")}
            tabIndex={wide ? 0 : -1}
            data-resizing={resizing ? "true" : undefined}
            onPointerDown={onGripPointerDown}
            onDoubleClick={resetPanelW}
            onKeyDown={onGripKeyDown}
            style={{ color: P.goldInk }}
          >
            <span className="cityview-grip-bar" aria-hidden />
          </div>
          <div aria-hidden style={{ height: 4, flexShrink: 0, background: "linear-gradient(90deg, var(--civic-red) 0 33%, var(--civic-parchment, #F4EEDC) 33% 66%, var(--civic-navy) 66%)" }} />
          <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: P.goldInk }}>
              <Compass style={{ width: 14, height: 14 }} /> {tr("hood.eyebrow", "The neighbourhood")}
            </div>
            <h3 style={{ fontFamily: serif, fontWeight: 400, fontSize: 20, margin: "4px 0 0", color: P.text, lineHeight: 1.2 }}>
              {hoodLoading ? tr("hood.loading", "Finding hospitals near {{place}}…", { place }) : tr("hood.title", "{{count}} hospitals within {{radius}} miles of {{name}}", { count: counts[hoodRadius].toLocaleString(), radius: hoodRadius, name: displayName })}
            </h3>
            {cheaperThanHere && (
              <p style={{ margin: "4px 0 0", fontSize: 12.5, color: P.muted }}>
                {tr("hood.cheaperLine", "{{n}} of the {{total}} ranked ones publish lower overall prices than {{name}}.", { n: cheaperThanHere.n, total: cheaperThanHere.total, name: displayName })}
              </p>
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
              {NEIGHBOUR_RADII.map((r) => (
                <button key={r} type="button" onClick={() => { setHoodRadius(r); setFocusId(null); }} aria-pressed={hoodRadius === r} style={segStyle(hoodRadius === r)}>
                  {tr("withinMi", "≤ {{r}} mi", { r })} · {counts[r]}
                </button>
              ))}
              <span style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center", fontSize: 11.5, color: P.muted }}>
                <span>{tr("sortBy", "Sort:")}</span>
                {(["distance", "price", "quality"] as HoodSort[]).map((k) => (
                  <button key={k} type="button" onClick={() => setHoodSort(k)} aria-pressed={hoodSort === k}
                    style={{ background: "transparent", border: 0, cursor: "pointer", padding: "2px 5px", fontFamily: sans, fontSize: 11.5, fontWeight: hoodSort === k ? 800 : 500, color: hoodSort === k ? P.goldInk : P.muted, textDecoration: hoodSort === k ? "underline" : "none", textUnderlineOffset: 3 }}>
                    {k === "distance" ? tr("sort.distance", "distance") : k === "price" ? (filter ? tr("sort.priceLow", "price") : tr("sort.price", "price grade")) : tr("sort.quality", "CMS stars")}
                  </button>
                ))}
              </span>
            </div>
          </div>

          <div className="cityview-panel-scroll" style={{ borderTop: `1px solid ${P.divider}` }}>
            {filter && filterRows ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 520 }}>
              <thead>
                <tr style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: P.muted, position: "sticky", top: 0, zIndex: 1, background: P.sheetSolid }}>
                  <th style={{ textAlign: "left", padding: "6px 16px", fontWeight: 800 }}>{tr("cols.hospital", "Hospital")}</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 800 }}>{tr("cols.miles", "Miles")}</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 800 }} title={tr("filter.colGradeTitle", "This hospital's price for the selected scenario or code vs the U.S. figure, on our A–F scale")}>{tr("filter.colGrade", "Grade for this")}</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 800 }}>{filter.kind === "scenario" ? tr("filter.colBill", "Complete bill") : tr("filter.colCharge", "Listed charge")}</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 800 }}>{tr("filter.colVsHere", "vs here")}</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 800 }}>{tr("cols.cms", "CMS stars")}</th>
                  <th style={{ textAlign: "right", padding: "6px 16px", fontWeight: 800 }} title={tr("cols.erTitle", "Median minutes from ER arrival to departure (CMS OP-18b)")}>{tr("cols.er", "ER wait")}</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: P.hereBg }}>
                  <td style={{ padding: "8px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <MapPin style={{ width: 13, height: 13, color: P.red, flexShrink: 0 }} />
                      <span style={{ fontWeight: 800 }}>{displayName}</span>
                      <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: P.goldInk }}>{tr("hood.here", "You are here")}</span>
                    </div>
                    <div style={{ fontSize: 11, color: P.muted, marginLeft: 21 }}>{place}</div>
                  </td>
                  <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono }}>0</td>
                  <td style={{ padding: "8px 8px" }}><GradeBadge markup={procMarkup(herePrice)} /></td>
                  <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono, fontWeight: 700 }}>{herePrice != null ? fmtUSD(herePrice) : <span style={{ color: P.faded, fontWeight: 400 }}>{tr("filter.noPriceHere", "no price on file")}</span>}</td>
                  <td style={{ padding: "8px 8px", color: P.faded }}>—</td>
                  <td style={{ padding: "8px 8px" }}>{num(hospital.cms_overall_rating) != null ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Star style={{ width: 11, height: 11, color: "var(--civic-gold)" }} fill="var(--civic-gold)" />{num(hospital.cms_overall_rating)}</span> : <span style={{ color: P.faded }}>—</span>}</td>
                  <td style={{ textAlign: "right", padding: "8px 16px", fontFamily: mono }}>{hereInfo?.ew != null ? fmtEr(hereInfo.ew) : <span style={{ color: P.faded }}>—</span>}</td>
                </tr>
                {sortedFilterRows.map((r) => {
                  const diff = herePrice != null && herePrice > 0 ? ((r.price - herePrice) / herePrice) * 100 : null;
                  const pct = diff != null ? `${diff >= 0 ? "+" : "−"}${Math.abs(Math.round(diff))}%` : null;
                  let vs: React.ReactNode;
                  if (r.verdict) {
                    const v = r.verdict;
                    if (v.kind === "worth_it") vs = <span style={{ color: P.goodInk, fontWeight: 700 }}>✓ {tr("filter.worthIt", "saves {{amt}} · worth the drive", { amt: fmtUSD(v.saving) })}</span>;
                    else if (v.kind === "marginal") vs = <span style={{ color: P.goldInk }}>~ {tr("filter.marginal", "{{pct}}, under the {{floor}} / 20% bar", { pct: pct ?? "cheaper", floor: fmtUSD(savingFloor(r.miles)) })}</span>;
                    else if (v.kind === "not_cheaper") vs = <span style={{ color: P.red, fontFamily: mono }}>{pct ?? tr("filter.notCheaper", "not cheaper")}</span>;
                    else if (v.kind === "unquotable") vs = <span style={{ color: P.faded }}>{tr("filter.unquotable", "fails the $200–$200,000 plausibility gate — no comparison")}</span>;
                    else vs = <span style={{ color: P.muted }}>{tr("filter.noBaseline", "priced — no saving claimed")}</span>;
                  } else {
                    vs = diff == null ? <span style={{ color: P.faded }}>—</span> : <span style={{ fontFamily: mono, fontWeight: 700, color: diff <= -10 ? P.goodInk : diff >= 10 ? P.red : P.muted }}>{pct}</span>;
                  }
                  return (
                    <tr key={r.id} ref={(el) => { if (el) rowRefs.current.set(r.id, el); else rowRefs.current.delete(r.id); }} onMouseEnter={() => setHighlightId(r.id)} onClick={() => focusRow(r.id)} aria-selected={focusId === r.id}
                      style={{ borderTop: `1px solid ${P.divider}`, background: highlightId === r.id ? P.hereBg : undefined, transition: "background 200ms ease", cursor: "pointer" }} className="hover:bg-black/[0.03] dark:hover:bg-white/[0.04]">
                      <td style={{ padding: "8px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontWeight: 600, color: P.text }}>{r.name}</span>
                          <Link to={`/hospital/${r.id}`} onClick={(e) => e.stopPropagation()} style={profileBtn}>{tr("row.profile", "Profile")} →</Link>
                        </div>
                        <div style={{ fontSize: 11, color: P.muted }}>{r.place}</div>
                      </td>
                      <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono }}>{fmtMiles(r.miles)}</td>
                      <td style={{ padding: "8px 8px" }}><GradeBadge markup={procMarkup(r.price)} /></td>
                      <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono, fontWeight: 700 }}>{fmtUSD(r.price)}</td>
                      <td style={{ padding: "8px 8px", fontSize: 12 }}>{vs}</td>
                      <td style={{ padding: "8px 8px" }}>{r.cs != null ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Star style={{ width: 11, height: 11, color: "var(--civic-gold)" }} fill="var(--civic-gold)" />{r.cs}</span> : <span style={{ color: P.faded }}>—</span>}</td>
                      <td style={{ textAlign: "right", padding: "8px 16px", fontFamily: mono }}>{r.ew != null ? fmtEr(r.ew) : <span style={{ color: P.faded }}>—</span>}</td>
                    </tr>
                  );
                })}
                {!filterLoading && sortedFilterRows.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: "14px 16px", color: P.muted }}>
                    {filterRows.unquotable > 0
                      ? tr("filter.allUnquotable", "{{u}} hospitals within {{radius}} miles price this, but every total falls outside the $200–$200,000 plausibility band, so no comparison is shown.", { u: filterRows.unquotable, radius: hoodRadius })
                      : tr("filter.none", "No hospital within {{radius}} miles publishes a usable price for this — no nearby comparison at this range.", { radius: hoodRadius })}
                  </td></tr>
                )}
                {filterLoading && sortedFilterRows.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: "14px 16px", color: P.muted }}><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-2" />{tr("filter.loading", "Pricing the neighbourhood…")}</td></tr>
                )}
              </tbody>
            </table>
            ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 560 }}>
              <thead>
                <tr style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: P.muted, position: "sticky", top: 0, zIndex: 1, background: P.sheetSolid }}>
                  <th style={{ textAlign: "left", padding: "6px 16px", fontWeight: 800 }}>{tr("cols.hospital", "Hospital")}</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 800 }}>{tr("cols.miles", "Miles")}</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 800 }}>{tr("cols.grade", "Price grade")}</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 800 }}>{tr("cols.markup", "vs U.S. median")}</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 800 }}>{tr("cols.cms", "CMS stars")}</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 800 }} title={tr("cols.recommendTitle", "% of the hospital's own surveyed patients who said they would definitely recommend it (CMS HCAHPS survey) — not our rating")}>{tr("cols.recommend", "Patients who'd recommend")}</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 800 }} title={tr("cols.erTitle", "Median minutes from ER arrival to departure (CMS OP-18b)")}>{tr("cols.er", "ER wait")}</th>
                  <th style={{ textAlign: "right", padding: "6px 16px", fontWeight: 800 }}>{tr("cols.google", "Google")}</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: P.hereBg }}>
                  <td style={{ padding: "8px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <MapPin style={{ width: 13, height: 13, color: P.red, flexShrink: 0 }} />
                      <span style={{ fontWeight: 800 }}>{displayName}</span>
                      <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: P.goldInk }}>{tr("hood.here", "You are here")}</span>
                    </div>
                    <div style={{ fontSize: 11, color: P.muted, marginLeft: 21 }}>{place}</div>
                  </td>
                  <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono }}>0</td>
                  <td style={{ padding: "8px 8px" }}><GradeBadge markup={priceMarkup} /></td>
                  <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono, color: priceMarkup != null && priceMarkup > 0 ? P.red : P.goodInk }}>{priceMarkup != null ? `${priceMarkup >= 0 ? "+" : "−"}${Math.abs(Math.round(priceMarkup)).toLocaleString()}%` : "—"}</td>
                  <td style={{ padding: "8px 8px" }}>{num(hospital.cms_overall_rating) != null ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Star style={{ width: 11, height: 11, color: "var(--civic-gold)" }} fill="var(--civic-gold)" />{num(hospital.cms_overall_rating)}</span> : <span style={{ color: P.faded }}>—</span>}</td>
                  <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono }}>{hereInfo?.rp != null ? `${hereInfo.rp}%` : <span style={{ color: P.faded }}>—</span>}</td>
                  <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono }}>{hereInfo?.ew != null ? fmtEr(hereInfo.ew) : <span style={{ color: P.faded }}>—</span>}</td>
                  <td style={{ textAlign: "right", padding: "8px 16px", fontFamily: mono }}>{num(hospital.google_rating) != null ? `${num(hospital.google_rating)!.toFixed(1)}★` : <span style={{ color: P.faded }}>—</span>}</td>
                </tr>
                {sortedNeighbours.map((n) => {
                  const m = markupOf(n.id);
                  return (
                    <tr key={n.id} ref={(el) => { if (el) rowRefs.current.set(n.id, el); else rowRefs.current.delete(n.id); }} onMouseEnter={() => setHighlightId(n.id)} onClick={() => focusRow(n.id)} aria-selected={focusId === n.id}
                      style={{ borderTop: `1px solid ${P.divider}`, background: highlightId === n.id ? P.hereBg : undefined, transition: "background 200ms ease", cursor: "pointer" }} className="hover:bg-black/[0.03] dark:hover:bg-white/[0.04]">
                      <td style={{ padding: "8px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontWeight: 600, color: P.text }}>{titleCaseHospital(n.n) || n.n}</span>
                          <Link to={`/hospital/${n.id}`} onClick={(e) => e.stopPropagation()} style={profileBtn}>{tr("row.profile", "Profile")} →</Link>
                        </div>
                        <div style={{ fontSize: 11, color: P.muted }}>{[titleCaseHospital(n.c) || n.c, n.s].filter(Boolean).join(", ")}</div>
                      </td>
                      <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono }}>{fmtMiles(n.miles)}</td>
                      <td style={{ padding: "8px 8px" }}><GradeBadge markup={m} /></td>
                      <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono, color: m == null ? P.faded : m > 0 ? P.red : P.goodInk }}>{m != null ? `${m >= 0 ? "+" : "−"}${Math.abs(Math.round(m)).toLocaleString()}%` : tr("hood.notRanked", "not ranked")}</td>
                      <td style={{ padding: "8px 8px" }}>{n.cs != null ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Star style={{ width: 11, height: 11, color: "var(--civic-gold)" }} fill="var(--civic-gold)" />{n.cs}</span> : <span style={{ color: P.faded }}>—</span>}</td>
                      <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono }}>{n.rp != null ? `${n.rp}%` : <span style={{ color: P.faded }}>—</span>}</td>
                      <td style={{ textAlign: "right", padding: "8px 8px", fontFamily: mono }}>{n.ew != null ? fmtEr(n.ew) : <span style={{ color: P.faded }}>—</span>}</td>
                      <td style={{ textAlign: "right", padding: "8px 16px", fontFamily: mono }}>{n.gr != null ? `${Number(n.gr).toFixed(1)}★` : <span style={{ color: P.faded }}>—</span>}</td>
                    </tr>
                  );
                })}
                {!hoodLoading && sortedNeighbours.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: "14px 16px", color: P.muted }}>{tr("hood.none", "No other hospital within {{radius}} miles — no nearby comparison at this range.", { radius: hoodRadius })}</td></tr>
                )}
              </tbody>
            </table>
            )}
            <div style={{ padding: "8px 16px 10px", fontSize: 10.5, color: P.faded }}>
              {filter && filterRows ? (
                <>
                  {bandNeighbours.length > filterRows.rows.length + (showUnquotable ? 0 : filterRows.unquotable)
                    ? tr("filter.foot", "{{missing}} nearby hospitals have no price on file for this. ", { missing: bandNeighbours.length - filterRows.rows.length - (showUnquotable ? 0 : filterRows.unquotable) })
                    : null}
                  {filter.kind === "scenario"
                    ? tr("filter.footScenario", "Complete bill = the hospital's own published prices for the scenario's codes, U.S. average for any it doesn't list. Grade = that bill vs the U.S. average on our A–F scale. Every priced hospital in the band is listed.")
                    : tr("filter.footCode", "Listed charge = median of the hospital's published lines for this code. Grade = that charge vs the U.S. median on our A–F scale. Every priced hospital in the band is listed.")}
                  {" "}{tr("hood.footSignals", "CMS stars and ER wait (median minutes from arrival to departure, CMS OP-18b) as published. Click a row to centre it on the map; Profile opens its page.")}
                </>
              ) : <>{tr("hood.foot", "Price grade and markup: each hospital's own published charges vs the U.S. median (A–F, ours). Patients who'd recommend = % of the hospital's own surveyed patients who said they would definitely recommend it (CMS HCAHPS) — the patients' verdict, not ours. CMS stars, Google: third-party ratings as published. Showing the {{max}} nearest.", { max: NEIGHBOUR_TABLE_MAX })} {tr("hood.footSignals", "CMS stars and ER wait (median minutes from arrival to departure, CMS OP-18b) as published. Click a row to centre it on the map; Profile opens its page.")}</>}
            </div>
          </div>

          {/* Hand-off to the per-scenario half on the Scenario Pricing tab */}
          <Link
            to={`/hospital/${hospital.id}/scenarios?radius=${hoodRadius}`}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", textDecoration: "none", borderTop: `1px solid ${P.divider}`, flexShrink: 0, color: P.text }}
            className="transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
          >
            <span style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(var(--civic-red-rgb),0.12)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Car style={{ width: 17, height: 17, color: P.red }} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontFamily: serif, fontSize: 17, color: P.text }}>{tr("cta.title", "Is it cheaper this far away?")}</span>
              <span style={{ display: "block", fontSize: 12, color: P.muted }}>{tr("cta.sub", "Pick any procedure on the Scenario Pricing tab and see which of these hospitals is worth the drive — priced from their own published files.")}</span>
            </span>
            <span style={{ marginLeft: "auto", color: P.goldInk, fontWeight: 800, whiteSpace: "nowrap", fontSize: 12.5 }}>{tr("cta.go", "Scenario Pricing →")}</span>
          </Link>
        </aside>
      </section>
    </div>
  );
}
